/**
 * Call Detail Modal - Shows full transcript details with video player
 *
 * Features:
 * - Video player with seek controls
 * - Clickable timestamps that jump to that point in the video
 * - AI summary of the call
 * - Speaker-labeled transcript
 * - Responsive layout for mobile and desktop
 */

import { useState, useEffect, useRef } from "react";
import { Play, Clock, FileText, Sparkles, ExternalLink, AlertCircle, DollarSign, User } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

interface TranscriptSentence {
  index: number;
  speakerName?: string;
  speakerId: number;
  text: string;
  rawText: string;
  startTime: number;
  endTime: number;
  sentiment?: string;
}

interface EocData {
  prospectName?: string;
  prospectEmail?: string;
  prospectNumber?: string;
  callOutcome?: string;
  cashCollected?: number;
  revenueGenerated?: number;
  closerName?: string;
  setterName?: string;
  callNotes?: string;
  fathomLink?: string;
  paymentPlan?: string;
  sourceContent?: string;
  dateCallWasTaken?: string;
}

interface TranscriptDetails {
  firefliesId: string;
  title: string;
  date: number;
  duration: number;
  videoUrl?: string;
  transcriptUrl: string;
  shortSummary?: string;
  eoc?: EocData | null;
  sentences: TranscriptSentence[];
}

interface CallDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  firefliesId: string | null;
}

// Format seconds to HH:MM:SS or MM:SS
const toTimestamp = (seconds: number): string => {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = Math.floor(seconds % 60);
  const parts = [
    h > 0 ? String(h).padStart(2, "0") : null,
    String(m).padStart(2, "0"),
    String(s).padStart(2, "0"),
  ].filter(Boolean) as string[];
  return parts.join(":");
};

// Format duration for display
const formatDuration = (secs: number): string => {
  const mins = Math.round(secs / 60);
  return `${mins} min`;
};

// Format date for display
const formatDate = (ms: number): string => {
  return new Date(ms).toLocaleDateString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
};

// Get sentiment color
const getSentimentColor = (sentiment?: string): string => {
  if (!sentiment) return "bg-muted";
  const s = sentiment.toLowerCase();
  if (s === "positive") return "bg-emerald-500/20 text-emerald-600";
  if (s === "negative") return "bg-rose-500/20 text-rose-600";
  return "bg-amber-500/20 text-amber-600";
};

// Get outcome badge class
const getOutcomeBadgeClass = (outcome: string): string => {
  const o = outcome.toLowerCase();
  if (o === "closed") return "bg-emerald-500/20 text-emerald-600 border border-emerald-500/30";
  if (o === "deposit collected") return "bg-emerald-500/20 text-emerald-600 border border-emerald-500/30";
  if (o === "upsell") return "bg-emerald-500/20 text-emerald-600 border border-emerald-500/30";
  if (o === "downsell" || o === "downsold") return "bg-amber-500/20 text-amber-600 border border-amber-500/30";
  if (o === "follow-up scheduled") return "bg-blue-500/20 text-blue-600 border border-blue-500/30";
  return "bg-muted text-muted-foreground border border-border";
};

export function CallDetailModal({ isOpen, onClose, firefliesId }: CallDetailModalProps) {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const transcriptRef = useRef<HTMLDivElement | null>(null);
  const [details, setDetails] = useState<TranscriptDetails | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeSentenceIndex, setActiveSentenceIndex] = useState<number | null>(null);

  // Fetch transcript details when modal opens
  useEffect(() => {
    if (!isOpen || !firefliesId) {
      setDetails(null);
      setError(null);
      return;
    }

    const fetchDetails = async () => {
      setIsLoading(true);
      setError(null);

      try {
        const res = await fetch(`/api/fireflies/get/${firefliesId}`);
        if (!res.ok) {
          const data = await res.json();
          throw new Error(data.error || `Failed to fetch: ${res.status}`);
        }

        const json = await res.json();
        if (!json.success || !json.data) {
          throw new Error("Transcript not found");
        }

        setDetails(json.data);
      } catch (e) {
        console.error("Error fetching transcript details:", e);
        setError(e instanceof Error ? e.message : "Failed to load transcript");
      } finally {
        setIsLoading(false);
      }
    };

    fetchDetails();
  }, [isOpen, firefliesId]);

  // Seek video to timestamp
  const onSeek = (time: number, sentenceIndex: number) => {
    const v = videoRef.current;
    if (v) {
      v.currentTime = Math.max(0, time);
      v.play().catch(() => undefined);
    }
    setActiveSentenceIndex(sentenceIndex);
  };

  // Track video time to highlight current sentence
  useEffect(() => {
    const v = videoRef.current;
    if (!v || !details?.sentences.length) return;

    const onTimeUpdate = () => {
      const currentTime = v.currentTime;
      const sentence = details.sentences.find(
        (s) => currentTime >= s.startTime && currentTime < s.endTime
      );
      if (sentence) {
        setActiveSentenceIndex(sentence.index);
      }
    };

    v.addEventListener("timeupdate", onTimeUpdate);
    return () => v.removeEventListener("timeupdate", onTimeUpdate);
  }, [details?.sentences]);

  // Scroll to active sentence
  useEffect(() => {
    if (activeSentenceIndex === null || !transcriptRef.current) return;

    const activeElement = transcriptRef.current.querySelector(
      `[data-sentence-index="${activeSentenceIndex}"]`
    );
    if (activeElement) {
      activeElement.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [activeSentenceIndex]);

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-6xl w-[95vw] max-h-[90vh] overflow-hidden p-0" onOpenAutoFocus={(e) => e.preventDefault()}>
        <DialogHeader className="px-4 sm:px-6 pt-4 sm:pt-6 pb-2">
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0 flex-1">
              <DialogTitle className="text-lg sm:text-xl font-bold truncate pr-8">
                {isLoading ? "Loading..." : details?.title || "Call Details"}
              </DialogTitle>
              {details && (
                <div className="flex items-center gap-3 mt-1 flex-wrap">
                  <p className="text-sm text-muted-foreground">
                    {formatDate(details.date)} &bull; {formatDuration(details.duration)}
                  </p>
                  {details.eoc?.callOutcome && (
                    <span className={`text-xs font-medium px-2.5 py-1 rounded-full ${getOutcomeBadgeClass(details.eoc.callOutcome)}`}>
                      {details.eoc.callOutcome}
                    </span>
                  )}
                  {details.eoc?.cashCollected != null && details.eoc.cashCollected > 0 && (
                    <span className="text-xs font-medium px-2.5 py-1 rounded-full bg-emerald-500/10 text-emerald-600 border border-emerald-500/20">
                      ${details.eoc.cashCollected.toLocaleString()} collected
                    </span>
                  )}
                </div>
              )}
            </div>
          </div>
        </DialogHeader>

        <div className="overflow-y-auto max-h-[calc(90vh-100px)] px-4 sm:px-6 pb-4 sm:pb-6">
          {/* Loading State */}
          {isLoading && (
            <div className="flex flex-col items-center justify-center py-16">
              <div className="w-8 h-8 border-4 border-[var(--brand-primary)]/20 border-t-[var(--brand-primary)] rounded-full animate-spin" />
              <p className="mt-4 text-sm text-muted-foreground">Loading transcript...</p>
            </div>
          )}

          {/* Error State */}
          {error && (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <AlertCircle className="h-12 w-12 text-destructive mb-4" />
              <p className="text-destructive font-medium">{error}</p>
              <Button variant="outline" onClick={onClose} className="mt-4">
                Close
              </Button>
            </div>
          )}

          {/* Content */}
          {details && !isLoading && !error && (
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 sm:gap-6">
              {/* Video + Transcript - Main Content */}
              <div className="lg:col-span-8 space-y-4">
                {/* Video Player */}
                <Card className="overflow-hidden">
                  <CardContent className="p-0">
                    {details.videoUrl ? (
                      <video
                        ref={videoRef}
                        src={details.videoUrl}
                        controls
                        className="w-full aspect-video bg-black"
                        playsInline
                      />
                    ) : (
                      <div className="w-full aspect-video bg-muted flex flex-col items-center justify-center gap-4">
                        <Play className="h-12 w-12 text-muted-foreground" />
                        <p className="text-muted-foreground text-sm">No video available</p>
                        <a
                          href={details.transcriptUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <Button variant="outline" size="sm">
                            <ExternalLink className="h-4 w-4 mr-2" />
                            Open in Fireflies
                          </Button>
                        </a>
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Transcript */}
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="flex items-center gap-2 text-base">
                      <FileText className="h-4 w-4 text-[var(--brand-primary)]" />
                      Transcript
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div
                      ref={transcriptRef}
                      className="max-h-[300px] sm:max-h-[400px] overflow-y-auto space-y-3 pr-2"
                    >
                      {details.sentences.length === 0 ? (
                        <p className="text-muted-foreground text-sm py-4 text-center">
                          No transcript available
                        </p>
                      ) : (
                        details.sentences.map((s) => (
                          <div
                            key={s.index}
                            data-sentence-index={s.index}
                            className={`flex gap-2 sm:gap-3 text-sm p-2 rounded-lg transition-colors ${
                              activeSentenceIndex === s.index
                                ? "bg-[var(--brand-primary)]/10 border-l-2 border-[var(--brand-primary)]"
                                : "hover:bg-muted/50"
                            }`}
                          >
                            <button
                              type="button"
                              onClick={() => onSeek(s.startTime, s.index)}
                              className="font-mono text-[var(--brand-primary)] hover:underline shrink-0 text-xs sm:text-sm"
                            >
                              {toTimestamp(s.startTime)}
                            </button>
                            <div className="min-w-0 flex-1">
                              <div className="flex items-center gap-2 mb-0.5">
                                <span className="text-xs font-medium text-muted-foreground truncate">
                                  {s.speakerName || `Speaker ${s.speakerId}`}
                                </span>
                                {s.sentiment && (
                                  <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${getSentimentColor(s.sentiment)}`}>
                                    {s.sentiment}
                                  </span>
                                )}
                              </div>
                              <div className="text-foreground break-words">{s.text}</div>
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Summary + Info - Sidebar */}
              <div className="lg:col-span-4 space-y-4">
                {/* AI Summary */}
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="flex items-center gap-2 text-base">
                      <Sparkles className="h-4 w-4 text-[var(--brand-primary)]" />
                      AI Summary
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-muted-foreground whitespace-pre-wrap">
                      {details.shortSummary || "No summary available for this call."}
                    </p>
                  </CardContent>
                </Card>

                {/* EOC Data */}
                {details.eoc && (
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="flex items-center gap-2 text-base">
                        <DollarSign className="h-4 w-4 text-[var(--brand-primary)]" />
                        EOC Response
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      {details.eoc.prospectName && (
                        <div className="flex justify-between text-sm">
                          <span className="text-muted-foreground">Prospect</span>
                          <span className="font-medium text-right max-w-[60%] truncate">{details.eoc.prospectName}</span>
                        </div>
                      )}
                      {details.eoc.callOutcome && (
                        <div className="flex justify-between text-sm">
                          <span className="text-muted-foreground">Outcome</span>
                          <span className={`font-medium px-2 py-0.5 rounded-full text-xs ${
                            details.eoc.callOutcome === "Closed" ? "bg-emerald-500/20 text-emerald-600" :
                            details.eoc.callOutcome === "Deposit Collected" ? "bg-emerald-500/20 text-emerald-600" :
                            details.eoc.callOutcome === "Upsell" ? "bg-emerald-500/20 text-emerald-600" :
                            details.eoc.callOutcome === "Downsell" || details.eoc.callOutcome === "Downsold" ? "bg-amber-500/20 text-amber-600" :
                            "bg-muted text-muted-foreground"
                          }`}>{details.eoc.callOutcome}</span>
                        </div>
                      )}
                      {(details.eoc.cashCollected != null && details.eoc.cashCollected > 0) && (
                        <div className="flex justify-between text-sm">
                          <span className="text-muted-foreground">Cash Collected</span>
                          <span className="font-medium text-emerald-600">${details.eoc.cashCollected.toLocaleString()}</span>
                        </div>
                      )}
                      {(details.eoc.revenueGenerated != null && details.eoc.revenueGenerated > 0) && (
                        <div className="flex justify-between text-sm">
                          <span className="text-muted-foreground">Revenue</span>
                          <span className="font-medium">${details.eoc.revenueGenerated.toLocaleString()}</span>
                        </div>
                      )}
                      {details.eoc.paymentPlan && (
                        <div className="flex justify-between text-sm">
                          <span className="text-muted-foreground">Payment Plan</span>
                          <span className="font-medium text-right max-w-[55%] truncate">{details.eoc.paymentPlan}</span>
                        </div>
                      )}
                      {details.eoc.closerName && (
                        <div className="flex justify-between text-sm">
                          <span className="text-muted-foreground">Closer</span>
                          <span className="font-medium">{details.eoc.closerName}</span>
                        </div>
                      )}
                      {details.eoc.setterName && (
                        <div className="flex justify-between text-sm">
                          <span className="text-muted-foreground">Setter</span>
                          <span className="font-medium">{details.eoc.setterName}</span>
                        </div>
                      )}
                      {details.eoc.sourceContent && (
                        <div className="flex justify-between text-sm">
                          <span className="text-muted-foreground">Source</span>
                          <span className="font-medium text-right max-w-[55%] truncate">{details.eoc.sourceContent}</span>
                        </div>
                      )}
                      {details.eoc.callNotes && (
                        <div className="pt-2 border-t">
                          <p className="text-xs text-muted-foreground mb-1 flex items-center gap-1">
                            <User className="h-3 w-3" /> Notes
                          </p>
                          <p className="text-sm text-foreground whitespace-pre-wrap">{details.eoc.callNotes}</p>
                        </div>
                      )}
                      {details.eoc.fathomLink && (
                        <div className="pt-2 border-t">
                          <a
                            href={details.eoc.fathomLink}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="w-full"
                          >
                            <Button variant="outline" size="sm" className="w-full">
                              <ExternalLink className="h-4 w-4 mr-2" />
                              Open Fathom Recording
                            </Button>
                          </a>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                )}

                {/* Call Info */}
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="flex items-center gap-2 text-base">
                      <Clock className="h-4 w-4 text-[var(--brand-primary)]" />
                      Call Info
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Duration</span>
                      <span className="font-medium">{formatDuration(details.duration)}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Date</span>
                      <span className="font-medium">
                        {new Date(details.date).toLocaleDateString()}
                      </span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Speakers</span>
                      <span className="font-medium">
                        {new Set(details.sentences.map(s => s.speakerName || `Speaker ${s.speakerId}`)).size}
                      </span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Segments</span>
                      <span className="font-medium">{details.sentences.length}</span>
                    </div>

                    <div className="pt-2 border-t">
                      <a
                        href={`https://app.fireflies.ai/view/${details.firefliesId}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="w-full"
                      >
                        <Button variant="outline" size="sm" className="w-full">
                          <ExternalLink className="h-4 w-4 mr-2" />
                          Open in Fireflies
                        </Button>
                      </a>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default CallDetailModal;
